/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useRef } from "react"
import Tippy from "@tippyjs/react"
import { Instance } from "tippy.js"
import { FloatingBox } from "./Misc/FloatingBox"
import { IComponentProp } from "@cd2/tools"
import { Menu } from "./Menu"

interface IProps {
  items: IComponentProp<typeof Menu, "items">
  children: React.ReactNode
  disabled?: boolean
  color?: string
  className?: string
  testId?: string
  // compact item padding/font size
  small?: boolean
  // caps the menu height and scrolls the overflow, e.g. "60vh"
  maxHeight?: string
}

export function DropdownMenu({
  items,
  children,
  disabled,
  color,
  className,
  testId,
  small,
  maxHeight,
}: IProps) {
  return (
    <React.Fragment>
      {disabled ? (
        <div>{children}</div>
      ) : (
        <InteractivePopout
          testId={testId}
          content={
            <FloatingBox
              css={css`
                ${FloatingBox.vars.childPadding}: 0;
                border: none;
                box-shadow: rgb(0 0 0 / 0%) 0px 0px 0px 0px, rgb(0 0 0 / 0%) 0px 0px 0px 0px,
                  rgb(0 0 0 / 12%) 0px 1px 1px 0px, rgb(60 66 87 / 16%) 0px 0px 0px 1px,
                  rgb(0 0 0 / 0%) 0px 0px 0px 0px, rgb(0 0 0 / 0%) 0px 0px 0px 0px,
                  rgb(60 66 87 / 8%) 0px 2px 5px 0px;
                overflow: hidden;
              `}
              className={className}
              color={color}
            >
              <div
                css={
                  maxHeight &&
                  css`
                    max-height: ${maxHeight};
                    overflow-y: auto;
                    overscroll-behavior: contain;
                  `
                }
              >
                <Menu items={items} small={small} />
              </div>
            </FloatingBox>
          }
          closeOnInsideClick
        >
          <div>{children}</div>
        </InteractivePopout>
      )}
    </React.Fragment>
  )
}

// Component that uses Tippy that when you click on the child, it opens the popout and that popout
// closes on any click
export function InteractivePopout({
  children,
  content,
  closeOnInsideClick,
  testId,
}: {
  children: React.ReactNode
  content: React.ReactNode
  closeOnInsideClick?: boolean
  testId?: string
}) {
  const instance = useRef<Instance>()

  return (
    <Tippy
      onCreate={e => (instance.current = e)}
      offset={[0, 2]}
      content={
        <div
          onClick={() => {
            if (closeOnInsideClick && instance.current) {
              instance.current.hide()
            }
          }}
        >
          {content}
        </div>
      }
      trigger="click"
      arrow={false}
      placement="bottom"
      appendTo={document.body}
      hideOnClick
      interactive
    >
      <div data-testid={testId}>{children}</div>
    </Tippy>
  )
}
